import { faker } from "@faker-js/faker/locale/fr"
import {
  type FamilyName,
  SalesDistribution,
  type SourceName,
  type SpecificFamilyName,
  type SpecificUniverseName,
  type UniverseName,
  familyEnum,
  sourceEnum,
  specificFamilyEnum,
  specificUniverseEnum,
  universeEnum,
} from "../../../types"
import type {
  ApiSales,
  ApiSpecificCategorySalesDetails,
  ApiSpecificSales,
  ApiUniverseSales,
  ApiUniverseSalesDetails,
  ApifinancialDataSalesSummary,
  SpecificCategoryFamily,
  UniverseFamily,
} from "../../sales"

const float = () => faker.number.float({ min: 0, max: 9_999_999, fractionDigits: 2 })
const fakeDate = () => faker.date.recent({ days: 365 }).toISOString().split("T")[0]
const maybe = <T>(val: T) => faker.helpers.maybe(() => val)
const evolution = () =>
  maybe(faker.number.float({ min: -2000, max: 2000, fractionDigits: 2 })) ?? null
const keyFigure = () => ({
  mainStat: maybe(float()) ?? null,
  evolution: evolution(),
})

export const mockSales = (): ApiSales => ({
  pharmacyId: faker.number.int({ min: 0, max: 9_999_999_999 }),
  laboratoryId: maybe(faker.number.int({ min: 0, max: 9_999_999 })) ?? null,
  sources: faker.helpers.arrayElements(Object.keys(sourceEnum) as SourceName[], {
    min: 1,
    max: 3,
  }),
  salesValue: maybe(keyFigure()),
  salesMargin: maybe(keyFigure()),
  periodStartDate: maybe(fakeDate()) ?? null,
  periodEndDate: maybe(fakeDate()) ?? null,
  units: maybe(keyFigure()) ?? null,
  numReferences: maybe(faker.number.int({ min: 0, max: 9_999 })) ?? null,
})

const universeMock = <
  T extends keyof typeof universeEnum | keyof typeof specificUniverseEnum,
>(
  name: T,
) => ({
  name,
  sales: maybe(keyFigure()) ?? null,
  uppSales: maybe(keyFigure()) ?? null,
  margin: maybe(keyFigure()) ?? null,
  uppMargin: maybe(keyFigure()) ?? null,
})

export const universesSalesMock = (): ApiUniverseSales => ({
  pharmacyId: faker.number.int({ min: 1000, max: 9999 }),
  periodStartDate: fakeDate(),
  periodEndDate: maybe(fakeDate()) ?? null,
  universes: (Object.keys(universeEnum) as Array<keyof typeof universeEnum>).map(
    universeMock,
  ),
})

export const specificSalesMock = (): ApiSpecificSales => ({
  pharmacyId: faker.number.int({ min: 1000, max: 9999 }),
  periodStartDate: fakeDate(),
  periodEndDate: maybe(fakeDate()) ?? null,
  specificCategories: (
    Object.keys(specificUniverseEnum) as Array<keyof typeof specificUniverseEnum>
  ).map(universeMock),
})

export const salesSummaryMock = (): ApifinancialDataSalesSummary => ({
  sales: keyFigure(),
})

const familiesOf = (universe: UniverseName) =>
  (Object.keys(familyEnum) as FamilyName[]).filter(
    (family) => familyEnum[family].universe === universe,
  )

const familyKeyFigure = () =>
  faker.helpers.maybe(() => ({ mainStat: float(), evolution: evolution() }), {
    probability: 0.85,
  }) ?? null

const sumOf = (
  families: Array<UniverseFamily | SpecificCategoryFamily>,
  distribution: SalesDistribution,
) =>
  families.reduce(
    (total, family) => total + (family[distribution]?.mainStat ?? 0),
    0,
  )

export const universeSalesDetailsMock = (
  universe: UniverseName,
): ApiUniverseSalesDetails => {
  const families = familiesOf(universe).map<UniverseFamily>((family) => ({
    family,
    sales: familyKeyFigure(),
    margin: familyKeyFigure(),
  }))

  return {
    pharmacyId: faker.number.int({ min: 1000, max: 9999 }),
    universe,
    periodStartDate: fakeDate(),
    periodEndDate: fakeDate(),
    sources: faker.helpers.arrayElements(Object.keys(sourceEnum) as SourceName[], {
      min: 1,
      max: 3,
    }),
    families,
    totalSales: {
      mainStat: sumOf(families, SalesDistribution.SALES),
      evolution: evolution(),
    },
    totalMargin: {
      mainStat: sumOf(families, SalesDistribution.MARGIN),
      evolution: evolution(),
    },
    uppSales: { mainStat: null, evolution: evolution() },
    uppMargin: { mainStat: null, evolution: evolution() },
  }
}

const specificFamiliesOf = (specificCategory: SpecificUniverseName) =>
  (Object.keys(specificFamilyEnum) as SpecificFamilyName[]).filter(
    (family) => specificFamilyEnum[family].specificCategory === specificCategory,
  )

export const specificCategorySalesDetailsMock = (
  specificCategory: SpecificUniverseName,
): ApiSpecificCategorySalesDetails => {
  const families = specificFamiliesOf(specificCategory).map<SpecificCategoryFamily>(
    (specificFamily) => ({
      specificFamily,
      sales: familyKeyFigure(),
      margin: familyKeyFigure(),
    }),
  )

  return {
    pharmacyId: faker.number.int({ min: 1000, max: 9999 }),
    specificCategory,
    periodStartDate: fakeDate(),
    periodEndDate: fakeDate(),
    sources: faker.helpers.arrayElements(Object.keys(sourceEnum) as SourceName[], {
      min: 1,
      max: 3,
    }),
    families,
    totalSales: {
      mainStat: sumOf(families, SalesDistribution.SALES),
      evolution: evolution(),
    },
    totalMargin: {
      mainStat: sumOf(families, SalesDistribution.MARGIN),
      evolution: evolution(),
    },
    uppSales: { mainStat: null, evolution: evolution() },
    uppMargin: { mainStat: null, evolution: evolution() },
  }
}
